import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  getAllAssociations,
} from "../../../../../services/associationsServices";

import {
  getAllRequests,
} from "../../../../../services/requestsServices";

import {
  buildSiteTargetFromAssociation,
  hasValidApiUrl,
  SITE_FAMILY_LABELS,
  SITE_FAMILY_ORDER,
  SITE_PRODUCT_LABELS,
} from "../utils/siteActivityConfig";

import {
  attachRequestsStatsToRows,
  buildRequestsStats,
  fetchSiteCriticalTarget,
  fetchSiteMetricsTarget,
  runWithConcurrency,
} from "../utils/siteActivityFetchers";

import {
  downloadCSV,
  safeIncludes,
  todayISO,
} from "../utils/siteActivityFormatters";

/* ==========================================================================
   CONSTANTES
   ========================================================================== */

const CRITICAL_CACHE_KEY =
  "site-activity-critical-cache-v3";

const ALERT_REFRESH_INTERVAL =
  2 * 60 * 1000;

const METRICS_REFRESH_INTERVAL =
  10 * 60 * 1000;

const SITE_CRITICAL_CONCURRENCY =
  4;

const SITE_METRICS_CONCURRENCY =
  3;

const DEFAULT_FILTERS = {
  search: "",
  productKey: "all",
  monitorStatus: "all",

  onlyWithAlerts: false,
  onlyWithRequests: false,
};

/* ==========================================================================
   HELPERS
   ========================================================================== */

function useDebouncedValue(
  value,
  delay = 250,
) {
  const [
    debounced,
    setDebounced,
  ] = useState(value);

  useEffect(() => {
    const timeout =
      setTimeout(
        () =>
          setDebounced(value),
        delay,
      );

    return () =>
      clearTimeout(timeout);
  }, [
    value,
    delay,
  ]);

  return debounced;
}

function readCriticalCache() {
  try {
    const raw =
      localStorage.getItem(
        CRITICAL_CACHE_KEY,
      );

    if (!raw) {
      return {};
    }

    const parsed =
      JSON.parse(raw);

    return (
      parsed &&
      typeof parsed ===
        "object"
        ? parsed
        : {}
    );
  } catch {
    return {};
  }
}

function writeCriticalCache(cache) {
  try {
    localStorage.setItem(
      CRITICAL_CACHE_KEY,
      JSON.stringify(cache),
    );
  } catch {
    // Aucun blocage du dashboard si localStorage échoue.
  }
}

function mergeRowsById(
  rows,
  patches,
) {
  const patchMap =
    new Map(
      patches.map(
        (patch) => [
          String(patch.id),
          patch,
        ],
      ),
    );

  return rows.map((row) => {
    const patch =
      patchMap.get(
        String(row.id),
      );

    if (!patch) {
      return row;
    }

    return {
      ...row,
      ...patch,
    };
  });
}

function createBaseRow(
  target,
  previousRow,
  dateStr,
) {
  const canKeepMetrics =
    previousRow &&
    previousRow.metricsLoaded &&
    previousRow.metricsDate ===
      dateStr;

  return {
    id: target.id,

    associationId:
      target.associationId,

    name:
      target.name,

    productKey:
      target.productKey,

    productLabel:
      target.productLabel,

    familyKey:
      target.familyKey,

    familyLabel:
      target.familyLabel,

    logo:
      target.logo,

    appUrl:
      target.appUrl,

    apiUrl:
      target.apiUrl,

    hasCriticalMonitoring:
      target.hasCriticalMonitoring,

    visitsMode:
      target.visitsMode,

    visitsModeLabel:
      target.visitsModeLabel,

    visitsToday:
      canKeepMetrics
        ? previousRow.visitsToday
        : null,

    visitsTotal:
      canKeepMetrics
        ? previousRow.visitsTotal
        : null,

    totalUsers:
      canKeepMetrics
        ? previousRow.totalUsers
        : null,

    todayUsers:
      canKeepMetrics
        ? previousRow.todayUsers
        : null,

    adherents:
      canKeepMetrics
        ? previousRow.adherents
        : null,

    inscrits:
      canKeepMetrics
        ? previousRow.inscrits
        : null,

    notifications:
      previousRow?.notifications ||
      [],

    messages:
      previousRow?.messages ||
      [],

    requests:
      previousRow?.requests ||
      [],

    requestsTotal:
      previousRow?.requestsTotal ||
      0,

    requestsPending:
      previousRow?.requestsPending ||
      0,

    requestsHigh:
      previousRow?.requestsHigh ||
      0,

    requestsInProgress: 0,
    requestsCompleted: 0,

    monitorStatus:
      target.hasCriticalMonitoring
        ? "checking"
        : "stats_only",

    criticalErrors: [],
    criticalSuccessKeys: [],

    criticalDataStale: false,

    metricErrors:
      canKeepMetrics
        ? previousRow.metricErrors ||
          []
        : [],

    metricsLoaded:
      !!canKeepMetrics,

    metricsDate:
      canKeepMetrics
        ? previousRow.metricsDate
        : null,

    lastCriticalSync:
      previousRow?.lastCriticalSync ||
      null,

    lastMetricsSync:
      canKeepMetrics
        ? previousRow.lastMetricsSync
        : null,
  };
}

/* ==========================================================================
   CACHE CRITIQUE

   Important :
   - messages => cache sans dépendance de date
   - notifications => cache uniquement pour la date correspondante

   Cela évite d'afficher une notification d'hier comme une notification
   d'aujourd'hui lorsqu'une API tombe temporairement.
   ========================================================================== */

function applyCriticalCache(
  rows,
  dateStr,
) {
  const cache =
    readCriticalCache();

  let cacheChanged =
    false;

  const nextRows =
    rows.map((row) => {
      const key =
        String(row.id);

      const previous =
        cache[key] || {};

      const successKeys =
        Array.isArray(
          row.criticalSuccessKeys,
        )
          ? row.criticalSuccessKeys
          : [];

      const notificationSuccess =
        successKeys.includes(
          "notifications",
        );

      const messageSuccess =
        successKeys.includes(
          "messages",
        );

      const hasNotificationError =
        row.criticalErrors?.some(
          (item) =>
            item.key ===
            "notifications",
        );

      const hasMessageError =
        row.criticalErrors?.some(
          (item) =>
            item.key ===
            "messages",
        );

      let notifications =
        row.notifications;

      let messages =
        row.messages;

      let stale = false;

      /* ------------------------------------------------------------------
         NOTIFICATIONS
         ------------------------------------------------------------------ */

      if (
        notificationSuccess
      ) {
        previous.notifications =
          row.notifications;

        previous.notificationsDate =
          dateStr;

        previous.notificationsAt =
          row.lastCriticalSync;

        cacheChanged = true;
      } else if (
        hasNotificationError &&
        previous.notificationsDate ===
          dateStr &&
        Array.isArray(
          previous.notifications,
        )
      ) {
        notifications =
          previous.notifications;

        stale = true;
      } else if (
        hasNotificationError
      ) {
        /*
         * On ne réutilise surtout pas les notifications
         * d'une autre journée.
         */
        notifications = [];
      }

      /* ------------------------------------------------------------------
         MESSAGES
         ------------------------------------------------------------------ */

      if (
        messageSuccess
      ) {
        previous.messages =
          row.messages;

        previous.messagesAt =
          row.lastCriticalSync;

        cacheChanged = true;
      } else if (
        hasMessageError &&
        Array.isArray(
          previous.messages,
        )
      ) {
        messages =
          previous.messages;

        stale = true;
      }

      if (
        notificationSuccess ||
        messageSuccess
      ) {
        previous.updatedAt =
          row.lastCriticalSync;

        cache[key] =
          previous;
      }

      return {
        ...row,

        notifications,
        messages,

        criticalDataStale:
          stale,
      };
    });

  if (cacheChanged) {
    writeCriticalCache(
      cache,
    );
  }

  return nextRows;
}

function getPreviousRowsMap(rows) {
  return new Map(
    rows.map((row) => [
      String(row.id),
      row,
    ]),
  );
}

function chooseFamily(
  targets,
  currentFamily,
) {
  const existingFamilies =
    new Set(
      targets.map(
        (target) =>
          target.familyKey,
      ),
    );

  if (
    currentFamily &&
    existingFamilies.has(
      currentFamily,
    )
  ) {
    return currentFamily;
  }

  const ordered =
    SITE_FAMILY_ORDER.find(
      (key) =>
        existingFamilies.has(
          key,
        ),
    );

  return (
    ordered ||
    targets[0]?.familyKey ||
    "autres"
  );
}

function isCanceledError(error) {
  return (
    error?.name === "CanceledError" ||
    error?.code === "ERR_CANCELED"
  );
}

function sumNumber(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return 0;
  }

  return Number(value) || 0;
}

/* ==========================================================================
   HOOK
   ========================================================================== */

export default function useSiteActivity() {
  const [
    rows,
    setRows,
  ] = useState([]);

  const [
    targets,
    setTargets,
  ] = useState([]);

  const [
    dateStr,
    setDateStr,
  ] = useState(
    todayISO(),
  );

  const [
    activeFamily,
    setActiveFamily,
  ] = useState(
    "monappliclub",
  );

  const [
    filters,
    setFilters,
  ] = useState(
    DEFAULT_FILTERS,
  );

  const [
    sortKey,
    setSortKey,
  ] = useState(
    "visitsToday",
  );

  const [
    sortDir,
    setSortDir,
  ] = useState(
    "desc",
  );

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    refreshing,
    setRefreshing,
  ] = useState(false);

  const [
    criticalRefreshing,
    setCriticalRefreshing,
  ] = useState(false);

  const [
    metricsLoadingFamily,
    setMetricsLoadingFamily,
  ] = useState(null);

  const [
    autoRefresh,
    setAutoRefresh,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  const [
    lastCriticalRefresh,
    setLastCriticalRefresh,
  ] = useState(null);

  const rowsRef =
    useRef([]);

  const targetsRef =
    useRef([]);

  const activeFamilyRef =
    useRef(
      activeFamily,
    );

  const fullRefreshAbortRef =
    useRef(null);

  const criticalAbortRef =
    useRef(null);

  const metricsAbortRef =
    useRef(null);

  const firstLoadRef =
    useRef(true);

  useEffect(() => {
    rowsRef.current =
      rows;
  }, [rows]);

  useEffect(() => {
    targetsRef.current =
      targets;
  }, [targets]);

  useEffect(() => {
    activeFamilyRef.current =
      activeFamily;
  }, [activeFamily]);

  const debouncedSearch =
    useDebouncedValue(
      filters.search,
      250,
    );

  /* ========================================================================
     MÉTRIQUES D'UNE FAMILLE
     ======================================================================== */

  const loadFamilyMetrics =
    useCallback(
      async (
        familyKey,
        {
          force = false,
        } = {},
      ) => {
        if (!familyKey) {
          return;
        }

        const currentTargets =
          targetsRef.current;

        if (!currentTargets.length) {
          return;
        }

        const currentRowsMap =
          getPreviousRowsMap(
            rowsRef.current,
          );

        const familyTargets =
          currentTargets.filter(
            (target) =>
              target.familyKey ===
              familyKey,
          );

        const targetsToLoad =
          force
            ? familyTargets
            : familyTargets.filter(
                (target) => {
                  const row =
                    currentRowsMap.get(
                      String(
                        target.id,
                      ),
                    );

                  return !(
                    row?.metricsLoaded &&
                    row?.metricsDate ===
                      dateStr
                  );
                },
              );

        if (!targetsToLoad.length) {
          return;
        }

        metricsAbortRef.current?.abort?.();

        metricsAbortRef.current =
          new AbortController();

        const signal =
          metricsAbortRef.current
            .signal;

        setMetricsLoadingFamily(
          familyKey,
        );

        try {
          const patches =
            await runWithConcurrency(
              targetsToLoad,
              SITE_METRICS_CONCURRENCY,
              (target) =>
                fetchSiteMetricsTarget(
                  target,
                  dateStr,
                  signal,
                ),
            );

          setRows((current) => {
            const next =
              mergeRowsById(
                current,
                patches,
              );

            rowsRef.current =
              next;

            return next;
          });
        } catch (metricsError) {
          if (
            !isCanceledError(
              metricsError,
            )
          ) {
            console.error(
              "Erreur statistiques famille :",
              metricsError,
            );
          }
        } finally {
          setMetricsLoadingFamily(
            (current) =>
              current ===
              familyKey
                ? null
                : current,
          );
        }
      },
      [dateStr],
    );

  /* ========================================================================
     REFRESH ALERTES UNIQUEMENT
     ======================================================================== */

  const refreshCriticalOnly =
    useCallback(async () => {
      const currentTargets =
        targetsRef.current;

      if (!currentTargets.length) {
        return;
      }

      criticalAbortRef.current?.abort?.();

      criticalAbortRef.current =
        new AbortController();

      const signal =
        criticalAbortRef.current
          .signal;

      setCriticalRefreshing(
        true,
      );

      try {
        const requestsPromise =
          Promise.resolve()
            .then(() =>
              getAllRequests(),
            )
            .catch(() => null);

        const criticalPatches =
          await runWithConcurrency(
            currentTargets,
            SITE_CRITICAL_CONCURRENCY,
            (target) =>
              fetchSiteCriticalTarget(
                target,
                dateStr,
                signal,
              ),
          );

        let nextRows =
          mergeRowsById(
            rowsRef.current,
            criticalPatches,
          );

        nextRows =
          applyCriticalCache(
            nextRows,
            dateStr,
          );

        const requests =
          await requestsPromise;

        if (
          Array.isArray(
            requests,
          )
        ) {
          const requestsStats =
            buildRequestsStats(
              requests,
            );

          nextRows =
            attachRequestsStatsToRows(
              nextRows,
              requestsStats,
            );
        }

        rowsRef.current =
          nextRows;

        setRows(
          nextRows,
        );

        setLastCriticalRefresh(
          new Date(),
        );
      } catch (refreshError) {
        if (
          !isCanceledError(
            refreshError,
          )
        ) {
          console.error(
            "Erreur actualisation alertes :",
            refreshError,
          );
        }
      } finally {
        setCriticalRefreshing(
          false,
        );
      }
    }, [dateStr]);

  /* ========================================================================
     REFRESH COMPLET

     Il ne dépend PLUS de activeFamily.
     Donc cliquer sur une autre famille ne recharge pas les alertes
     des 59 sites.
     ======================================================================== */

  const refresh =
    useCallback(async () => {
      fullRefreshAbortRef.current?.abort?.();
      criticalAbortRef.current?.abort?.();
      metricsAbortRef.current?.abort?.();

      fullRefreshAbortRef.current =
        new AbortController();

      const signal =
        fullRefreshAbortRef.current
          .signal;

      setRefreshing(true);
      setError("");

      if (firstLoadRef.current) {
        setLoading(true);
      }

      try {
        const [
          associationsResult,
          requestsResult,
        ] =
          await Promise.allSettled([
            getAllAssociations(),
            getAllRequests(),
          ]);

        if (
          associationsResult.status !==
            "fulfilled" ||
          !Array.isArray(
            associationsResult.value,
          )
        ) {
          throw new Error(
            "Impossible de récupérer la liste des sites.",
          );
        }

        const associations =
          associationsResult.value;

        const requests =
          requestsResult.status ===
            "fulfilled" &&
          Array.isArray(
            requestsResult.value,
          )
            ? requestsResult.value
            : null;

        const nextTargets =
          associations
            .filter(
              hasValidApiUrl,
            )
            .map(
              buildSiteTargetFromAssociation,
            );

        const previousRowsMap =
          getPreviousRowsMap(
            rowsRef.current,
          );

        let baseRows =
          nextTargets.map(
            (target) =>
              createBaseRow(
                target,
                previousRowsMap.get(
                  String(
                    target.id,
                  ),
                ),
                dateStr,
              ),
          );

        const criticalPatches =
          await runWithConcurrency(
            nextTargets,
            SITE_CRITICAL_CONCURRENCY,
            (target) =>
              fetchSiteCriticalTarget(
                target,
                dateStr,
                signal,
              ),
          );

        baseRows =
          mergeRowsById(
            baseRows,
            criticalPatches,
          );

        baseRows =
          applyCriticalCache(
            baseRows,
            dateStr,
          );

        if (requests) {
          const requestsStats =
            buildRequestsStats(
              requests,
            );

          baseRows =
            attachRequestsStatsToRows(
              baseRows,
              requestsStats,
            );
        }

        const effectiveFamily =
          chooseFamily(
            nextTargets,
            activeFamilyRef.current,
          );

        targetsRef.current =
          nextTargets;

        setTargets(
          nextTargets,
        );

        if (
          effectiveFamily !==
          activeFamilyRef.current
        ) {
          activeFamilyRef.current =
            effectiveFamily;

          setActiveFamily(
            effectiveFamily,
          );
        }

        rowsRef.current =
          baseRows;

        setRows(
          baseRows,
        );

        setLoading(false);

        setLastCriticalRefresh(
          new Date(),
        );

        const familyTargets =
          nextTargets.filter(
            (target) =>
              target.familyKey ===
              effectiveFamily,
          );

        if (familyTargets.length) {
          setMetricsLoadingFamily(
            effectiveFamily,
          );

          const metricPatches =
            await runWithConcurrency(
              familyTargets,
              SITE_METRICS_CONCURRENCY,
              (target) =>
                fetchSiteMetricsTarget(
                  target,
                  dateStr,
                  signal,
                ),
            );

          const finalRows =
            mergeRowsById(
              baseRows,
              metricPatches,
            );

          rowsRef.current =
            finalRows;

          setRows(
            finalRows,
          );
        }
      } catch (refreshError) {
        if (
          !isCanceledError(
            refreshError,
          )
        ) {
          console.error(
            "Erreur chargement activité des sites :",
            refreshError,
          );

          setError(
            refreshError?.message ||
              "Impossible de charger l’activité des sites.",
          );
        }
      } finally {
        setLoading(false);
        setRefreshing(false);

        setMetricsLoadingFamily(
          null,
        );

        firstLoadRef.current =
          false;
      }
    }, [dateStr]);

  /* ========================================================================
     PREMIER CHARGEMENT / DATE
     ======================================================================== */

  useEffect(() => {
    refresh();

    return () => {
      fullRefreshAbortRef.current?.abort?.();
    };
  }, [refresh]);

  /* ========================================================================
     CHANGEMENT DE FAMILLE

     Uniquement les statistiques de cette famille.
     ======================================================================== */

  useEffect(() => {
    if (
      firstLoadRef.current ||
      refreshing
    ) {
      return;
    }

    loadFamilyMetrics(
      activeFamily,
      {
        force: false,
      },
    );
  }, [
    activeFamily,
    refreshing,
    loadFamilyMetrics,
  ]);

  /* ========================================================================
     AUTO REFRESH ALERTES
     ======================================================================== */

  useEffect(() => {
    if (!autoRefresh) {
      return undefined;
    }

    const interval =
      setInterval(
        () => {
          refreshCriticalOnly();
        },
        ALERT_REFRESH_INTERVAL,
      );

    return () =>
      clearInterval(interval);
  }, [
    autoRefresh,
    refreshCriticalOnly,
  ]);

  /* ========================================================================
     AUTO REFRESH STATISTIQUES
     ======================================================================== */

  useEffect(() => {
    if (!autoRefresh) {
      return undefined;
    }

    const interval =
      setInterval(
        () => {
          loadFamilyMetrics(
            activeFamily,
            {
              force: true,
            },
          );
        },
        METRICS_REFRESH_INTERVAL,
      );

    return () =>
      clearInterval(interval);
  }, [
    autoRefresh,
    activeFamily,
    loadFamilyMetrics,
  ]);

  /* ========================================================================
     FAMILLES
     ======================================================================== */

  const familyOptions =
    useMemo(() => {
      const map =
        new Map();

      rows.forEach(
        (row) => {
          const key =
            row.familyKey ||
            "autres";

          const current =
            map.get(key) || {
              value: key,

              label:
                SITE_FAMILY_LABELS[
                  key
                ] ||
                SITE_FAMILY_LABELS.autres,

              sites: 0,
              notifications: 0,
              messages: 0,
              requests: 0,
              warnings: 0,
            };

          current.sites += 1;

          current.notifications +=
            Number(
              row.notifications
                ?.length || 0,
            );

          current.messages +=
            Number(
              row.messages
                ?.length || 0,
            );

          current.requests +=
            Number(
              row.requestsPending ||
                0,
            );

          if (
            row.monitorStatus ===
              "warning" ||
            row.monitorStatus ===
              "offline"
          ) {
            current.warnings += 1;
          }

          map.set(
            key,
            current,
          );
        },
      );

      return SITE_FAMILY_ORDER
        .map(
          (key) =>
            map.get(key),
        )
        .filter(Boolean);
    }, [rows]);

  const activeFamilyLabel =
    SITE_FAMILY_LABELS[
      activeFamily
    ] ||
    SITE_FAMILY_LABELS.autres;

  const familyRows =
    useMemo(
      () =>
        rows.filter(
          (row) =>
            row.familyKey ===
            activeFamily,
        ),
      [
        rows,
        activeFamily,
      ],
    );

  /* ========================================================================
     PRODUITS
     ======================================================================== */

  const productOptions =
    useMemo(() => {
      const keys =
        Array.from(
          new Set(
            familyRows
              .map(
                (row) =>
                  row.productKey,
              )
              .filter(Boolean),
          ),
        );

      return [
        {
          value: "all",
          label:
            "Tous les produits",
        },

        ...keys.map(
          (key) => ({
            value: key,

            label:
              SITE_PRODUCT_LABELS[
                key
              ] || key,
          }),
        ),
      ];
    }, [familyRows]);

  /* ========================================================================
     FILTRAGE
     ======================================================================== */

  const filteredRows =
    useMemo(() => {
      const search =
        debouncedSearch
          .trim()
          .toLowerCase();

      const filtered =
        familyRows.filter(
          (row) => {
            const matchesSearch =
              !search ||
              safeIncludes(
                row.name,
                search,
              ) ||
              safeIncludes(
                row.productLabel,
                search,
              ) ||
              safeIncludes(
                row.appUrl,
                search,
              );

            const matchesProduct =
              filters.productKey ===
                "all" ||
              row.productKey ===
                filters.productKey;

            const matchesMonitorStatus =
              filters.monitorStatus ===
                "all" ||
              row.monitorStatus ===
                filters.monitorStatus;

            const hasAlerts =
              Number(
                row.notifications
                  ?.length || 0,
              ) > 0 ||
              Number(
                row.messages
                  ?.length || 0,
              ) > 0;

            const matchesAlerts =
              !filters.onlyWithAlerts ||
              hasAlerts;

            const matchesRequests =
              !filters.onlyWithRequests ||
              Number(
                row.requestsPending ||
                  0,
              ) > 0;

            return (
              matchesSearch &&
              matchesProduct &&
              matchesMonitorStatus &&
              matchesAlerts &&
              matchesRequests
            );
          },
        );

      const direction =
        sortDir === "asc"
          ? 1
          : -1;

      filtered.sort(
        (a, b) => {
          if (
            sortKey === "name"
          ) {
            return (
              String(
                a.name || "",
              ).localeCompare(
                String(
                  b.name || "",
                ),
              ) * direction
            );
          }

          if (
            sortKey ===
            "productLabel"
          ) {
            return (
              String(
                a.productLabel ||
                  "",
              ).localeCompare(
                String(
                  b.productLabel ||
                    "",
                ),
              ) * direction
            );
          }

          const rawA =
            a[sortKey];

          const rawB =
            b[sortKey];

          const nullA =
            rawA === null ||
            rawA === undefined;

          const nullB =
            rawB === null ||
            rawB === undefined;

          if (
            nullA &&
            !nullB
          ) {
            return 1;
          }

          if (
            !nullA &&
            nullB
          ) {
            return -1;
          }

          if (
            nullA &&
            nullB
          ) {
            return String(
              a.name || "",
            ).localeCompare(
              String(
                b.name || "",
              ),
            );
          }

          const valueA =
            Number(rawA || 0);

          const valueB =
            Number(rawB || 0);

          if (
            valueA === valueB
          ) {
            return String(
              a.name || "",
            ).localeCompare(
              String(
                b.name || "",
              ),
            );
          }

          return (
            (
              valueA <
              valueB
                ? -1
                : 1
            ) * direction
          );
        },
      );

      return filtered;
    }, [
      familyRows,
      debouncedSearch,
      filters,
      sortKey,
      sortDir,
    ]);

  /* ========================================================================
     TOTAL TABLE
     ======================================================================== */

  const tableTotals =
    useMemo(() => {
      return filteredRows.reduce(
        (acc, row) => {
          acc.sites += 1;

          acc.visitsToday +=
            sumNumber(
              row.visitsToday,
            );

          acc.visitsTotal +=
            sumNumber(
              row.visitsTotal,
            );

          acc.totalUsers +=
            sumNumber(
              row.totalUsers,
            );

          acc.todayUsers +=
            sumNumber(
              row.todayUsers,
            );

          acc.adherents +=
            sumNumber(
              row.adherents,
            );

          acc.inscrits +=
            sumNumber(
              row.inscrits,
            );

          acc.notifications +=
            Number(
              row.notifications
                ?.length || 0,
            );

          acc.messages +=
            Number(
              row.messages
                ?.length || 0,
            );

          acc.requestsPending +=
            Number(
              row.requestsPending ||
                0,
            );

          if (
            row.monitorStatus ===
              "warning" ||
            row.monitorStatus ===
              "offline"
          ) {
            acc.monitorWarnings +=
              1;
          }

          return acc;
        },
        {
          sites: 0,

          visitsToday: 0,
          visitsTotal: 0,

          totalUsers: 0,
          todayUsers: 0,

          adherents: 0,
          inscrits: 0,

          notifications: 0,
          messages: 0,

          requestsPending: 0,

          monitorWarnings: 0,
        },
      );
    }, [filteredRows]);

  /* ========================================================================
     TOTAL DASHBOARD
     ======================================================================== */

  const dashboardTotals =
    useMemo(() => {
      const globalAlerts =
        rows.reduce(
          (acc, row) => {
            /*
             * Notifications :
             * uniquement celles du jour sélectionné.
             */
            acc.notifications +=
              Number(
                row.notifications
                  ?.length || 0,
              );

            /*
             * Messages :
             * tous les non lus.
             */
            acc.messages +=
              Number(
                row.messages
                  ?.length || 0,
              );

            /*
             * Requêtes :
             * toutes les pending.
             */
            acc.requestsPending +=
              Number(
                row.requestsPending ||
                  0,
              );

            if (
              row.monitorStatus ===
                "warning" ||
              row.monitorStatus ===
                "offline"
            ) {
              acc.monitorWarnings +=
                1;
            }

            return acc;
          },
          {
            notifications: 0,
            messages: 0,
            requestsPending: 0,
            monitorWarnings: 0,
          },
        );

      return {
        sites:
          rows.length,

        familySites:
          filteredRows.length,

        visitsToday:
          tableTotals.visitsToday,

        visitsTotal:
          tableTotals.visitsTotal,

        totalUsers:
          tableTotals.totalUsers,

        todayUsers:
          tableTotals.todayUsers,

        adherents:
          tableTotals.adherents,

        inscrits:
          tableTotals.inscrits,

        notifications:
          globalAlerts.notifications,

        messages:
          globalAlerts.messages,

        requestsTotal:
          globalAlerts.requestsPending,

        requestsPending:
          globalAlerts.requestsPending,

        monitorWarnings:
          globalAlerts.monitorWarnings,
      };
    }, [
      rows,
      filteredRows,
      tableTotals,
    ]);

  /* ========================================================================
     GRAPHIQUES
     ======================================================================== */

  const topSitesData =
    useMemo(() => {
      return [
        ...filteredRows,
      ]
        .filter(
          (row) =>
            row.visitsToday !==
              null &&
            row.visitsToday !==
              undefined,
        )
        .sort(
          (a, b) =>
            Number(
              b.visitsToday ||
                0,
            ) -
            Number(
              a.visitsToday ||
                0,
            ),
        )
        .slice(0, 10)
        .map(
          (row) => ({
            name:
              row.name,

            value:
              Number(
                row.visitsToday ||
                  0,
              ),

            productLabel:
              row.productLabel,
          }),
        );
    }, [filteredRows]);

  const productChartData =
    useMemo(() => {
      const map =
        new Map();

      filteredRows.forEach(
        (row) => {
          const key =
            row.productKey ||
            "unknown";

          const current =
            map.get(key) || {
              key,

              name:
                row.productLabel ||
                SITE_PRODUCT_LABELS.unknown,

              sites: 0,
              visits: 0,
            };

          current.sites += 1;

          current.visits +=
            sumNumber(
              row.visitsToday,
            );

          map.set(
            key,
            current,
          );
        },
      );

      return Array.from(
        map.values(),
      ).sort(
        (a, b) =>
          b.sites - a.sites,
      );
    }, [filteredRows]);

  /* ========================================================================
     FILTRES
     ======================================================================== */

  const updateFilter =
    useCallback(
      (key, value) => {
        setFilters(
          (previous) => ({
            ...previous,
            [key]: value,
          }),
        );
      },
      [],
    );

  const resetFilters =
    useCallback(() => {
      setFilters(
        DEFAULT_FILTERS,
      );
    }, []);

  const selectFamily =
    useCallback(
      (familyKey) => {
        activeFamilyRef.current =
          familyKey;

        setActiveFamily(
          familyKey,
        );

        setFilters(
          (previous) => ({
            ...previous,

            productKey:
              "all",
          }),
        );
      },
      [],
    );

  /* ========================================================================
     TRI
     ======================================================================== */

  const toggleSort =
    useCallback((key) => {
      setSortKey(
        (previous) => {
          if (
            previous === key
          ) {
            setSortDir(
              (currentDir) =>
                currentDir ===
                "asc"
                  ? "desc"
                  : "asc",
            );

            return previous;
          }

          setSortDir(
            key === "name" ||
              key ===
                "productLabel"
              ? "asc"
              : "desc",
          );

          return key;
        },
      );
    }, []);

  const setQuickSortKey =
    useCallback((key) => {
      setSortKey(key);

      setSortDir(
        key === "name" ||
          key ===
            "productLabel"
          ? "asc"
          : "desc",
      );
    }, []);

  /* ========================================================================
     EXPORT
     ======================================================================== */

  const exportCSV =
    useCallback(() => {
      const headers = [
        "Famille",
        "Site",
        "Produit",

        "Visites du jour",
        "Visites totales",

        "Utilisateurs",
        "Nouveaux utilisateurs",

        "Adhérents",
        "Inscrits",

        "Notifications du jour",
        "Messages non lus",

        "Requêtes en attente",

        "Surveillance",

        "URL",
      ];

      const csvRows =
        filteredRows.map(
          (row) => [
            row.familyLabel,

            row.name,

            row.productLabel,

            row.visitsToday ??
              "",

            row.visitsTotal ??
              "",

            row.totalUsers ??
              "",

            row.todayUsers ??
              "",

            row.adherents ??
              "",

            row.inscrits ??
              "",

            row.notifications
              ?.length || 0,

            row.messages?.length ||
              0,

            row.requestsPending ||
              0,

            row.monitorStatus,

            row.appUrl || "",
          ],
        );

      downloadCSV(
        `activite_${activeFamily}_${dateStr}.csv`,
        headers,
        csvRows,
      );
    }, [
      filteredRows,
      activeFamily,
      dateStr,
    ]);

  return {
    rows,
    filteredRows,

    tableTotals,
    dashboardTotals,

    familyOptions,

    activeFamily,
    activeFamilyLabel,

    selectFamily,

    productOptions,

    productChartData,
    topSitesData,

    dateStr,
    setDateStr,

    filters,
    updateFilter,
    resetFilters,

    sortKey,
    sortDir,

    toggleSort,
    setQuickSortKey,

    loading,
    refreshing,

    criticalRefreshing,

    metricsLoadingFamily,

    autoRefresh,
    setAutoRefresh,

    lastCriticalRefresh,

    error,

    refresh,
    refreshCriticalOnly,

    exportCSV,
  };
}